"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import RotateHandle from "../ui/RotateHandle";
import { useWindowStore } from "../windows/windowStore";

type ProtractorProps = {
  windowId?: string;
  radiusPx?: number;
};

export default function Protractor({
  windowId,
  radiusPx = 300,
}: ProtractorProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  // Use ratio-based geometry derived from the given radius
  const R = radiusPx;
  const margin = Math.max(8, R * 0.03);
  const bodyThickness = Math.max(6, R * 0.033);
  // Standard-like tick proportions: major:medium:minor ≈ 1.0 : 0.6 : 0.35
  const majorTickLength = Math.max(12, R * 0.1);
  const mediumTickLength = Math.max(8, majorTickLength * 0.6);
  const minorTickLength = Math.max(5, majorTickLength * 0.35);
  const labelPaddingOuter = Math.max(6, R * 0.025);
  const labelRadius = R - majorTickLength - labelPaddingOuter;
  const strokeMajor = Math.max(1.25, R * 0.0045);
  const strokeMedium = Math.max(0.9, R * 0.003);
  const strokeMinor = Math.max(0.6, R * 0.002);
  const labelFontSize = Math.max(9, R * 0.035);

  const [size, setSize] = useState({
    width: R * 2 + margin * 2,
    height: R + margin + bodyThickness,
  });
  const rotationDeg = useWindowStore((s) =>
    windowId ? s.windows[windowId]?.rotationDeg ?? 0 : 0
  );
  const updateRotation = useWindowStore((s) => s.updateRotation);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (!cr) return;
      setSize((s) => ({
        width: Math.max(cr.width, R * 2 + margin * 2),
        height: s.height,
      }));
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, [R, margin]);

  const ticks = useMemo(() => {
    const arr: { angle: number; kind: "major" | "medium" | "minor" }[] = [];
    for (let deg = 0; deg <= 180; deg += 1) {
      const kind =
        deg % 10 === 0 ? "major" : deg % 5 === 0 ? "medium" : "minor";
      arr.push({ angle: deg, kind });
    }
    return arr;
  }, []);

  const cx = R + margin;
  const cy = R + margin;

  return (
    <div
      ref={containerRef}
      className="relative h-full w-full select-none text-foreground"
      style={{ background: "transparent" }}
      aria-label="Protractor"
    >
      {windowId && (
        <RotateHandle
          className="no-drag absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-6 w-6 rounded-full bg-card/70 border border-border shadow"
          title="Rotate"
          initialDeg={rotationDeg}
          getCenter={() => {
            const root = containerRef.current;
            if (!root) return null;
            const rect = root.getBoundingClientRect();
            return {
              x: rect.left + rect.width / 2,
              y: rect.top + rect.height / 2,
            };
          }}
          onChange={(deg) => updateRotation(windowId, deg)}
        />
      )}
      <svg
        width={size.width}
        height={size.height}
        role="img"
        aria-label="Protractor graphic"
        style={{ display: "block" }}
      >
        <g shapeRendering="geometricPrecision">
          <path
            d={`M ${cx - R} ${cy} A ${R} ${R} 0 0 1 ${cx + R} ${cy} L ${
              cx + R
            } ${cy + bodyThickness} L ${cx - R} ${cy + bodyThickness} Z`}
            fill="rgba(255,255,255,0.05)"
            stroke="currentColor"
            strokeOpacity={0.6}
            strokeWidth={Math.max(0.5, R * 0.0015)}
          />
          {/* Tick marks on the rim: 1° (minor), 5° (medium), 10° (major) */}
          {ticks.map(({ angle, kind }) => {
            const rad = (angle * Math.PI) / 180;
            const len =
              kind === "major"
                ? majorTickLength
                : kind === "medium"
                ? mediumTickLength
                : minorTickLength;
            const x1 = cx + Math.cos(Math.PI - rad) * (R - len);
            const y1 = cy - Math.sin(rad) * (R - len);
            const x2 = cx + Math.cos(Math.PI - rad) * R;
            const y2 = cy - Math.sin(rad) * R;
            return (
              <line
                key={angle}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="currentColor"
                strokeOpacity={0.8}
                strokeWidth={
                  kind === "major"
                    ? strokeMajor
                    : kind === "medium"
                    ? strokeMedium
                    : strokeMinor
                }
              />
            );
          })}
          {/* Major angle labels: outer 0→180, inner 180→0 */}
          {ticks
            .filter((t) => t.kind === "major")
            .map(({ angle }) => {
              const rad = (angle * Math.PI) / 180;
              const outerR = labelRadius;
              const innerR =
                labelRadius - Math.max(labelFontSize * 1.15, R * 0.055);
              const outerX = cx + Math.cos(Math.PI - rad) * outerR;
              const outerY = cy - Math.sin(rad) * outerR;
              const innerX = cx + Math.cos(Math.PI - rad) * innerR;
              const innerY = cy - Math.sin(rad) * innerR;
              const innerValue = 180 - angle;
              return (
                <g key={`lbl-${angle}`}>
                  <text
                    x={outerX}
                    y={outerY}
                    fontSize={labelFontSize}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill="currentColor"
                    fillOpacity={0.85}
                  >
                    {angle}
                  </text>
                  <text
                    x={innerX}
                    y={innerY}
                    fontSize={labelFontSize}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill="currentColor"
                    fillOpacity={0.65}
                  >
                    {innerValue}
                  </text>
                </g>
              );
            })}

          {/* Inner arc to mirror common protractor guides */}
          <path
            d={`M ${cx - (R - majorTickLength)} ${cy} A ${
              R - majorTickLength
            } ${R - majorTickLength} 0 0 1 ${cx + (R - majorTickLength)} ${cy}`}
            fill="none"
            stroke="currentColor"
            strokeOpacity={0.35}
            strokeWidth={Math.max(0.5, R * 0.001)}
          />

          {/* Baseline (diameter) */}
          <line
            x1={cx - R}
            y1={cy}
            x2={cx + R}
            y2={cy}
            stroke="currentColor"
            strokeOpacity={0.5}
            strokeWidth={Math.max(1, R * 0.002)}
          />

          {/* Interior radial guide lines at each 10° */}
          {ticks
            .filter((t) => t.kind === "major")
            .map(({ angle }) => {
              const rad = (angle * Math.PI) / 180;
              const xEnd = cx + Math.cos(Math.PI - rad) * (R - majorTickLength);
              const yEnd = cy - Math.sin(rad) * (R - majorTickLength);
              const isAxis = angle === 0 || angle === 90 || angle === 180;
              return (
                <line
                  key={`rad-${angle}`}
                  x1={cx}
                  y1={cy}
                  x2={xEnd}
                  y2={yEnd}
                  stroke="currentColor"
                  strokeOpacity={isAxis ? 0.5 : 0.25}
                  strokeWidth={
                    isAxis ? Math.max(1, R * 0.002) : Math.max(0.5, R * 0.001)
                  }
                />
              );
            })}

          {/* Fine interior lines at each degree for a realistic grid */}
          {ticks
            .filter((t) => t.kind !== "major")
            .map(({ angle }) => {
              const rad = (angle * Math.PI) / 180;
              const xEnd = cx + Math.cos(Math.PI - rad) * (R - minorTickLength);
              const yEnd = cy - Math.sin(rad) * (R - minorTickLength);
              return (
                <line
                  key={`rad-min-${angle}`}
                  x1={cx}
                  y1={cy}
                  x2={xEnd}
                  y2={yEnd}
                  stroke="currentColor"
                  strokeOpacity={0.15}
                  strokeWidth={Math.max(0.5, R * 0.0008)}
                />
              );
            })}

          {/* Center hole */}
          <circle
            cx={cx}
            cy={cy}
            r={Math.max(2, R * 0.02)}
            fill="none"
            stroke="currentColor"
            strokeOpacity={0.6}
            strokeWidth={Math.max(0.75, R * 0.0015)}
          />
        </g>
      </svg>
    </div>
  );
}
